import clsx from "clsx";
import { GridLoader } from "react-spinners";
import { useIdentityStore, useSnackbarStore } from "../../../core/store";
import { UploadFile } from "../../components/drag-drop/model/UploadFile";
import { FileService } from "../../components/drag-drop/services/file.service";
import { CContainer } from "@coreui/react-pro";
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoaderContext } from "../../components/loader/context/LoaderContext";
import { useTranslation } from "react-i18next";
import { useWebPage, useSEO } from "../../../core/hooks/db/fisioteduca/useFisioteduca";
import * as MessageSnackbarDefault from "../../../core/constants/message-snackbar/msgFormDefault";
import { Util } from "../../../core/util/util";
import { WebPage } from "../../../core/models/db/web-page";
import { GiSpartanHelmet } from "react-icons/gi";
import BookForm from "./BookForm";

const SpartanDetail = () => {
    const { t } = useTranslation();
    const { identity } = useIdentityStore((state) => state);
    const navigate = useNavigate();

    const [webPage, setWebPage] = useState<WebPage>(undefined);
    const [loading, setLoading] = useState(false);
    const [formHeight, setFormHeight] = useState(0);
    const formRef = useRef(null);
    const [typeAction, setTypeAction] = useState<"add" | "update">(null);

    const { handleLoading } = useContext(LoaderContext);
    const { addSnackbar, setMaxSnackbars } = useSnackbarStore();

    const { getWebPageByType, addWebPage: add, updateWebPage: update } = useWebPage();
    const { checkSpecificParam: checkSpecificParamSEO } = useSEO(); // Si tienes un método para SEO

    const { id } = useParams();

    useEffect(() => {
        if (formRef.current) {
            setFormHeight(formRef.current.clientHeight);
        }
    }, [loading, webPage]);

    /**
     * Cargar WebPage al iniciar
     */
    useEffect(() => {
        (async () => {
            setLoading(true);

            const result = await getWebPageByType("BOOK");
            if (result && result.ok && result.item) {
                setWebPage(result.item);
                setTypeAction("update");
            } else {
                // Crear nuevo
                let webPage = new WebPage();
                setWebPage(webPage);
                setTypeAction("add");
            }
            setLoading(false);
        })();
    }, []);

    /**
     * Creación de archivo (subida)
     */
    const createFile = async (file: any, id: string) => {
        try {
            const { data } = await FileService.add(new UploadFile(file, ["images", "program", id]), null);
            if (data.ok) {
                const pathRelative = `/${data.item[0].destination}/${data.item[0].filename}`;
                return pathRelative;
            } else {
                return undefined;
            }
        } catch (e) {
            throw e;
        }
    };

    // Función auxiliar para convertir un blob a archivo y subirlo
    const convertBlob = async (
        blobStr: string,
        programId: string,
        prefix: string
    ): Promise<string> => {
        try {
            // Puedes ajustar la detección de extensión según lo necesites
            const extension = ".png";
            const fileName = `${prefix}_${Date.now()}${extension}`;
            const file = await Util.transformBlobToFile(blobStr, fileName);
            const { data } = await FileService.add(
                new UploadFile(file, ["images", "program", programId]),
                null
            );
            if (data.ok && data.item?.[0]) {
                return `/${data.item[0].destination}/${data.item[0].filename}`;
            }
        } catch (error) {
            console.error("Error converting blob:", error);
        }
        // Si ocurre algún error, se retorna el blob original
        return blobStr;
    };

    // Función principal para convertir todos los blobs dentro del JSON de contenido
    const convertAllBlobsFromJson = async (
        jsonString: string,
        programId: string
    ): Promise<string> => {
        try {
            // Parseamos el JSON
            const contentArray = JSON.parse(jsonString);

            // Iteramos sobre cada bloque de contenido
            for (const block of contentArray) {
                // Procesamos el campo "image"
                if (block.image && typeof block.image === "string" && block.image.startsWith("blob:")) {
                    // Usamos un timestamp único para este campo
                    block.image = await convertBlob(block.image, programId, `program_image_${Date.now()}`);
                }
                // Procesamos el campo "listImage"
                if (block.listImage && typeof block.listImage === "string" && block.listImage.startsWith("blob:")) {
                    block.listImage = await convertBlob(block.listImage, programId, `program_listImage_${Date.now()}`);
                }
                // Procesamos el array "imageTextItems"
                if (block.imageTextItems && Array.isArray(block.imageTextItems)) {
                    await Promise.all(
                        block.imageTextItems.map(async (item, i) => {
                            if (item.image && typeof item.image === "string" && item.image.startsWith("blob:")) {
                                let randomNumber = Math.floor(Math.random() * 100000 + 1);

                                // Cada imagen obtiene su propio timestamp
                                item.image = await convertBlob(item.image, programId, `program_imageText_${i}_${randomNumber}`);
                            }
                        })
                    );
                }
                // Si en el futuro se agregan otros campos que puedan contener blobs, agrégalos aquí.
            }


            return JSON.stringify(contentArray);
        } catch (error) {
            console.error("Error al convertir blobs en JSON:", error);
            return jsonString;
        }
    };

    /**
     * Update
     */
    const updateWebPage = useCallback(async (
        program: WebPage,
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => {
        try {
            setLoading(true);
            const prCopy = { ...program };
            const { oldImage, image, nameImage } = imageItem;

            // Manejo de imagen en blob
            if (image && image.startsWith("blob")) {
                const lastDotIndex = nameImage.lastIndexOf(".");
                // const name = lastDotIndex > -1 ? nameImage.slice(0, lastDotIndex) : nameImage;
                const name = Date.now() + "_" + Math.random().toString(36).substring(7);

                const ext = lastDotIndex > -1 ? nameImage.slice(lastDotIndex) : ".png";
                const formattedName = name.replace(/ /g, "_");
                let file = await Util.transformBlobToFile(
                    image,
                    formattedName + "_" + Date.now() + ext
                );
                prCopy.image = await createFile(file, prCopy.id);
            }

            // Si la imagen se ha subido correctamente, eliminar la anterior (si procede)
            if (oldImage && oldImage !== prCopy.image) {
                await FileService.delete(oldImage);
            }

            if (prCopy?.jsonContent) {
                console.log("entro aqui? program jsonContent", prCopy.jsonContent);
                prCopy.jsonContent = await convertAllBlobsFromJson(
                    prCopy.jsonContent,
                    prCopy.id
                );
            }

            const result = await update(prCopy);
            if (result.ok) {
                setWebPage(result.item);
            }

            //  Mostrar mensaje de éxito
            addSnackbar(MessageSnackbarDefault.successEditDefault);

        } catch (error) {
            console.error("Error al actualizar WebPage", error);
            addSnackbar(MessageSnackbarDefault.errorDefault);
        } finally {
            setLoading(false);
        }
    }, [update, addSnackbar]);

    /**
     * Add
     */
    const addWebPage = useCallback(async (
        program: WebPage,
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => {
        try {
            setLoading(true);

            // 1. Crear una copia del programa sin la imagen principal y el jsonContent
            const programToCreate = { ...program };
            delete programToCreate.image;
            delete programToCreate.jsonContent;

            // 2. Crear el WebPage sin imágenes
            const createResult = await add(programToCreate);
            if (!createResult.ok) {
                throw new Error("Error al crear el WebPage");
            }

            const newWebPage = createResult.item;
            setTypeAction("update");
            setWebPage(newWebPage);

            const newWebPageId = newWebPage.id;

            // 3. Subir la imagen principal si existe y es un blob
            let mainImagePath = newWebPage.image;
            if (imageItem.image && imageItem.image.startsWith("blob:")) {

                // let fileName = `program_main_${Date.now()}.png`;
                // const file = await Util.transformBlobToFile(imageItem.image, fileName);
                // mainImagePath = await createFile(file, newWebPageId);
                const lastDotIndex = imageItem.nameImage.lastIndexOf(".");
                // const name = lastDotIndex > -1 ? imageItem.nameImage.slice(0, lastDotIndex) : imageItem.nameImage;
                const name = Date.now() + "_" + Math.random().toString(36).substring(7);
                const ext = lastDotIndex > -1 ? imageItem.nameImage.slice(lastDotIndex) : ".png";
                const formattedName = name.replace(/ /g, "_");
                let file = await Util.transformBlobToFile(
                    imageItem.image,
                    formattedName + "_" + Date.now() + ext
                );
                mainImagePath = await createFile(file, newWebPageId);
            }

            // 4. Subir las imágenes secundarias dentro de jsonContent
            let updatedJsonContent = program.jsonContent;
            if (updatedJsonContent) {
                updatedJsonContent = await convertAllBlobsFromJson(updatedJsonContent, newWebPageId);
            }


            // 5. Actualizar el WebPage con las rutas de las imágenes
            const updatedWebPage = {
                ...newWebPage,
                image: mainImagePath,
                jsonContent: updatedJsonContent
            };

            const updateResult = await update(updatedWebPage);
            if (updateResult.ok) {
                setWebPage(updateResult.item);
            } else {
                throw new Error("Error al actualizar el WebPage con las imágenes");
            }

            // 6. Mostrar mensaje de éxito
            addSnackbar(MessageSnackbarDefault.successAddDefault);

        } catch (error) {
            console.error("Error al agregar WebPage", error);
            addSnackbar(MessageSnackbarDefault.errorDefault);
        } finally {
            setLoading(false);
        }
    }, [add, update, addSnackbar]);



    /**
     * Manejo final en el formulario
     */
    const handleRecord = (
        value: WebPage,
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => {

        // SIEMPRE ES BOOK EN ESTE CASO
        value.pageType = "BOOK";

        if (typeAction === "add" && !value?.id) {
            addWebPage(value, imageItem);
        } else if (typeAction === "update" && value?.id !== undefined) {
            updateWebPage(value, imageItem);
        } else {
            console.warn("Error, no hay acción definida");
        }
    };

    return (
        <CContainer style={{ display: "flex", flexDirection: "column" }}>
            <div className="w-full justify-between items-center flex pt-4 pb-5">
                <div className="flex flex-col">


                    <span className="flex text-4xl gap-2" style={{ fontWeight: 500 }}>
                        {"Página Mi Libro"} 
                    </span>
                    <span className="small text-medium-emphasis">
                        {typeAction === "add"
                            ? "Crear Página Mi Libro"
                            : "Detalle Página Mi Libro"
                        }
                    </span>
                </div>
            </div>

            <div className="w-full flex justify-center mb-10">
                <div className="w-full flex justify-center gap-4 flex-col xl:flex-row ">
                    <div
                        className={clsx(`flex flex-col w-full justify-center items-center`, {
                            "xl:w-[100%]": webPage?.id,
                        })}
                    >
                        <div className="bg-white block p-4 md:p-7 shadow-md md:rounded-lg w-full md:w-[70%]">
                            <div className="text-2xl font-medium mb-4">
                                {"Completa los datos"}
                            </div>
                            <div className="w-full flex flex-col md:flex-row justify-center md:gap-4 ">
                                {loading && (
                                    <div
                                        className="w-full flex flex-col justify-center items-center"
                                        style={{ height: 547 }}
                                    >
                                        <GridLoader color="#3d4f65" size={25} speedMultiplier={0.7} />
                                    </div>
                                )}

                                {!loading && webPage && (
                                    <div ref={formRef} className="p-0 w-full">
                                        <BookForm
                                            initialWebPage={webPage}
                                            onSubmit={(webPage, imageItem) => {

                                                console.log("webPage", webPage);
                                                handleRecord(webPage, imageItem);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CContainer>
    );
};

export default SpartanDetail;
